// Wave 20.49q — Turn a MediaPipe segmentation mask into a small
// PNG data URL suitable for stashing on the session summary and
// rendering as a per-session thumbnail on PositionDetail.
//
// The mask is an ImageData whose alpha channel encodes the
// per-pixel person-vs-background probability (255 = definitely
// rider, 0 = background). We downsample to a small square,
// color the foreground with the brand green, then encode as a
// PNG data URL. Callers cap the base64 size at ~30 KB — inputs
// bigger than that get rejected.

const TARGET_SIZE = 128;
const FG_R = 106;
const FG_G = 255;
const FG_B = 69;
const FG_A = 217; // 0.85 * 255

/**
 * Downsample + colorize a segmentation-mask ImageData into a
 * brand-green silhouette PNG data URL. Returns null when the
 * input mask is missing or the encoded string exceeds `maxBytes`
 * (default 30 KB base64) — caller falls back to no snapshot in
 * that case.
 */
export function silhouetteToPngDataUrl(
  mask: ImageData | null | undefined,
  maxBytes: number = 30 * 1024,
): string | null {
  if (!mask || mask.width <= 0 || mask.height <= 0) return null;

  // Downsample to TARGET_SIZE while preserving the mask's aspect
  // ratio, then centre inside a TARGET_SIZE × TARGET_SIZE canvas
  // so every thumbnail is the same shape.
  const srcW = mask.width;
  const srcH = mask.height;
  const scale = Math.min(TARGET_SIZE / srcW, TARGET_SIZE / srcH);
  const dstW = Math.max(1, Math.round(srcW * scale));
  const dstH = Math.max(1, Math.round(srcH * scale));
  const offX = Math.floor((TARGET_SIZE - dstW) / 2);
  const offY = Math.floor((TARGET_SIZE - dstH) / 2);

  const out = new Uint8ClampedArray(TARGET_SIZE * TARGET_SIZE * 4);
  const src = mask.data;

  for (let y = 0; y < dstH; y++) {
    const srcY = Math.min(srcH - 1, Math.floor((y / dstH) * srcH));
    for (let x = 0; x < dstW; x++) {
      const srcX = Math.min(srcW - 1, Math.floor((x / dstW) * srcW));
      // MediaPipe encodes person-probability on the alpha
      // channel (and sometimes on R). Use whichever is
      // stronger.
      const si = (srcY * srcW + srcX) * 4;
      const a = Math.max(src[si + 3] ?? 0, src[si] ?? 0);
      if (a < 96) continue; // clearly background — leave transparent
      const di = ((y + offY) * TARGET_SIZE + (x + offX)) * 4;
      out[di] = FG_R;
      out[di + 1] = FG_G;
      out[di + 2] = FG_B;
      out[di + 3] = Math.min(FG_A, Math.max(0, a));
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = TARGET_SIZE;
  canvas.height = TARGET_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const image = new ImageData(out, TARGET_SIZE, TARGET_SIZE);
  ctx.putImageData(image, 0, 0);
  const dataUrl = canvas.toDataURL('image/png');
  if (dataUrl.length > maxBytes) return null;
  return dataUrl;
}

// Wave 20.49s — compute the rider's frontal area (real m²) from a
// MediaPipe segmentation mask + a px/m scale. Used at Preflight
// lock time to snapshot A_cal — the rider's cross-section in
// the guided upright pose. Session-to-session normalization on
// the backend compares each new session's A_cal against the
// rider's running median to derive a rig-neutral CdA scale.
//
// The rider's A_cal in the same guided upright pose should be
// constant across sessions. Real deviations come from camera
// distance, angle, lens, or lighting — all things we want to
// wash out at the CdA display layer.
export function frontalAreaFromMask(
  mask: ImageData | null | undefined,
  pxPerM: number,
): number | null {
  if (!mask || mask.width <= 0 || mask.height <= 0) return null;
  if (!Number.isFinite(pxPerM) || pxPerM <= 10) return null;
  let count = 0;
  const src = mask.data;
  for (let i = 0; i < src.length; i += 4) {
    // Same person-probability threshold as the PNG encoder so
    // A_cal ≈ area of the silhouette actually shown to the rider.
    const a = Math.max(src[i + 3] ?? 0, src[i] ?? 0);
    if (a >= 96) count++;
  }
  if (count < 100) return null;
  const m2 = count / (pxPerM * pxPerM);
  if (!Number.isFinite(m2) || m2 <= 0 || m2 > 3) return null;
  return m2;
}
